import { SubflowMessengerAction } from 'lib-shared-types-api';
import { useCallback, useEffect, useState } from 'react';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import {
  SaveStatusData,
  useSaveStatusState,
} from 'components/settings/components/sections/dixaMessenger/views/flow/hooks/useSaveStatusState';
import { NodeDataT } from 'fable/flowEditor';
import { FlowsGetItemResponseT, FlowT } from 'hocs/flows/api/types';

import { FlowMessengerAction } from '../flow.types';

import { formatSubflowNodesForSubflowEditor } from './formatSubflowNodesForSubflowEditor';

type FlowDataGetter = () =>
  | Promise<FlowsGetItemResponseT<FlowMessengerAction, SubflowMessengerAction>>
  | undefined;

export type UseFlowReturnValue = {
  actions: FlowMessengerAction[];
  active: boolean;
  messengerId: string | undefined;
  setActions: (actions: FlowMessengerAction[]) => void;
  setActive: (active: boolean) => void;
  subflows: DixaMessengerSubflows;
  setSubflows: (subflows: DixaMessengerSubflows) => void;
  localCopyOfFlow: MessengerFlow | undefined;
  mainFlowSaveStatusData: SaveStatusData;
};

export type DixaMessengerSubflow = NodeDataT<SubflowMessengerAction[]>;
export type DixaMessengerSubflows = DixaMessengerSubflow[];
type MessengerFlow = FlowT<FlowMessengerAction, SubflowMessengerAction>;

export function useFlowData(getFlowData: FlowDataGetter): UseFlowReturnValue {
  const [localCopyOfFlow, setLocalCopyOfFlow] = useState<MessengerFlow>();
  const [editedActions, setActions] = useState<FlowMessengerAction[]>([]);
  const [active, setActive] = useState(true);
  const [messengerId, setMessengerId] = useState<string | undefined>();
  const mainFlowSaveStatusData = useSaveStatusState();

  const findDeletedSubflows = useCallback(
    (actions: FlowMessengerAction[]): string[] => {
      const referencesToSubflows = actions
        .filter((action) => action.nodeType === FlowNodeTypes.SUBFLOW_REFERENCE)
        .map((action) => action.id)
        // remove the duplicates
        .filter((value, index, self) => self.indexOf(value) === index);

      // if there are no references to subflows, we can just remove all of the subflows
      if (referencesToSubflows.length === 0) {
        return localCopyOfFlow?.subflows?.map((subflow) => subflow.id) ?? [];
      }

      const subflows = localCopyOfFlow?.subflows ?? [];

      const subflowIds = subflows.map((subflow) => subflow.id) ?? [];

      const deletedSubflowIds = subflowIds.filter((id) =>
        // keep the onces that are referenced
        referencesToSubflows.includes(id),
      );

      return deletedSubflowIds;
    },
    [localCopyOfFlow],
  );

  const handleSetActions = useCallback(
    (actions: FlowMessengerAction[]) => {
      mainFlowSaveStatusData.setStatusToUnsaved();
      setActions(actions);

      const deletedSubflowIds = findDeletedSubflows(actions);

      if (deletedSubflowIds.length > 0) {
        const newSubflows =
          localCopyOfFlow?.subflows?.filter(
            (subflow) => !deletedSubflowIds.includes(subflow.id),
          ) ?? [];

        setLocalCopyOfFlow((localCopyOfFlow) => ({
          ...localCopyOfFlow!,
          actions,
          subflows: newSubflows,
        }));
      }

      setLocalCopyOfFlow((localCopyOfFlow) => ({
        ...localCopyOfFlow!,
        actions,
      }));
    },
    [
      setLocalCopyOfFlow,
      setActions,
      mainFlowSaveStatusData,
      findDeletedSubflows,
      localCopyOfFlow?.subflows,
    ],
  );

  const handleSetSubflows = useCallback(
    (subflows: DixaMessengerSubflows) => {
      mainFlowSaveStatusData.setStatusToUnsaved();
      setLocalCopyOfFlow((localCopyOfFlow) => ({
        ...localCopyOfFlow!,
        subflows,
      }));
    },
    [mainFlowSaveStatusData],
  );

  const handleSetActive = useCallback((active: boolean) => {
    setActive(active);
    setLocalCopyOfFlow((localCopyOfFlow) => ({
      ...localCopyOfFlow!,
      active,
    }));
  }, []);

  useEffect(() => {
    getFlowData()
      ?.then((res) => {
        if (!res.data) return;
        if (res.data.channel !== 'messenger') return;

        const formattedFlowObject = formatSubflowNodesForSubflowEditor(
          res.data,
        );

        setLocalCopyOfFlow(formattedFlowObject);
        setActions(
          formattedFlowObject.actions ? [...formattedFlowObject.actions] : [],
        );
        setActive(formattedFlowObject.active || false);
        setActive(res.data.active);
        setMessengerId(res.data.location);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [getFlowData]);

  return {
    localCopyOfFlow,
    actions: editedActions,
    active: active,
    messengerId,
    setActions: handleSetActions,
    setActive: handleSetActive,
    // TODO: create a subflow list object that allows for things link "updateSubflowById" or "getSubflowById" etc.
    subflows: localCopyOfFlow?.subflows || [],
    setSubflows: handleSetSubflows,
    mainFlowSaveStatusData,
  };
}
